import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { BibleReader } from './components/bible-reader';
import { bibleState } from './state/bible-state';
import { formatChapterReference, formatAbbrevChapterReference } from '@skjv/common';
import { VerseMenu } from './components/verse-menu/verse-menu';
import { PericopePopover } from './components/pericope-popover';
import { NoteEditor } from './components/notes/note-editor';
import { BookmarkCreator } from './components/bookmarks/bookmark-creator';
import { UserDataPanel } from './components/user-data/user-data-panel';
import { StudyPanel } from './components/study-panel';
import { StrongsPanel } from '@components/strongs-panel';
import { VerseAnalysisPanel } from './components/verse-analysis-panel';
import { ChatPanel } from './components/chat-panel';
import { ConfirmDialog, DesktopUpdateToast, Toast } from './components/dialogs';
import { AppHeader } from './app-header';
import { useScrollTracking } from './use-scroll-tracking';
import { parseChatParam, clearChatParam } from '@utils/index';
import { FirstRunSetup } from './components/desktop-setup/first-run-setup';
import { getDesktop, isDesktop } from '@/desktop/desktop';
import { useDesktopUpdates } from '@/desktop/use-desktop-updates';

/**
 * Main application component.
 */
const App = observer(function App() {
  const [isChapterChanged, setIsChapterChanged] = useState(false);

  const [displayedReference, setDisplayedReference] = useState(() =>
    formatChapterReference(bibleState.currentLocation),
  );
  const [abbreviatedReference, setAbbreviatedReference] = useState(() =>
    formatAbbrevChapterReference(bibleState.currentLocation),
  );

  // Scroll tracking and chapter detection
  useScrollTracking(setDisplayedReference, setAbbreviatedReference, setIsChapterChanged);

  // Desktop shell: update offers arrive through the house ConfirmDialog.
  useDesktopUpdates();

  // SKJV Desktop opens onto the study library (UAT 2026-08-27) — the app
  // is the study home now. Skipped on first run (the setup ask leads) and
  // never on the web, which has no study surface.
  useEffect(() => {
    if (isDesktop() && getDesktop()?.settings) {
      bibleState.showStudyPanel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // First-run guided tour: auto-opens once per install (skjv-guide-seen
  // flag; skipping or finishing sets it). FirstRunSetup reloads the app
  // when it completes, so a plain mount effect still catches the true
  // first load. Native-only — the tour walks the study surface.
  useEffect(() => {
    if (isDesktop() && getDesktop()?.settings && !bibleState.hasSeenGuide) {
      bibleState.startGuide();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync `dark` class on <html> so portaled content (modals/dialogs
  // rendered to document.body) participates in Tailwind dark mode.
  useEffect(() => {
    document.documentElement.classList.toggle('dark', bibleState.isDarkMode);
  }, [bibleState.isDarkMode]);

  // Handle ?chat= deep link on app load
  useEffect(() => {
    const chatMessage = parseChatParam();
    if (!chatMessage) return;

    const timer = setTimeout(() => {
      bibleState.clearChatHistory();
      bibleState.showChatPanel();
      bibleState.sendChatMessage(chatMessage);
      clearChatParam();
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  const handleReferenceUpdate = (full: string, abbrev: string) => {
    setDisplayedReference(full);
    setAbbreviatedReference(abbrev);
  };

  return (
    <div className={bibleState.isDarkMode ? 'dark' : ''}>
      <div className="min-h-screen bg-white dark:bg-gray-800">
        {/* Header */}
        <AppHeader
          displayedReference={displayedReference}
          abbreviatedReference={abbreviatedReference}
          isChapterChanged={isChapterChanged}
          onReferenceUpdate={handleReferenceUpdate}
        />

        {/* Main Content */}
        <main className="app-main">
          <BibleReader />
        </main>

        {/* Verse Analysis Panel — before StrongsPanel so Strong's wins paint order */}
        {bibleState.verseAnalysisDisplay && (
          <VerseAnalysisPanel
            analysis={bibleState.verseAnalysisDisplay.analysis}
            location={bibleState.verseAnalysisDisplay.location}
            isVisible={bibleState.verseAnalysisDisplay.isVisible}
            isSplit={!!bibleState.strongsDisplay?.isVisible}
            onClose={() => bibleState.hideVerseAnalysis()}
          />
        )}

        {/* Strong's Definition Panel */}
        {bibleState.strongsDisplay && (
          <StrongsPanel
            numbers={bibleState.strongsDisplay.numbers}
            isVisible={bibleState.strongsDisplay.isVisible}
            onClose={() => bibleState.hideStrongsDefinition()}
            englishWord={bibleState.strongsDisplay.englishWord}
            websterDefinition={bibleState.strongsDisplay.websterDefinition}
            isSplit={!!bibleState.verseAnalysisDisplay?.isVisible}
          />
        )}

        {/* Overlays & Panels */}
        <VerseMenu />
        <PericopePopover />
        {isDesktop() && <NoteEditor />}
        <UserDataPanel />
        {isDesktop() && <StudyPanel />}
        <ChatPanel />
        {isDesktop() && <BookmarkCreator />}
        <ConfirmDialog />
        <Toast />
        {isDesktop() && <DesktopUpdateToast />}
        {isDesktop() && !getDesktop()?.settings && <FirstRunSetup />}
      </div>
    </div>
  );
});

export default App;
